<script lang="ts">
  import { addCredits } from '../../lib/components/map/credits';
  import { onMount, onDestroy, tick, untrack } from 'svelte';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../../lib/api/client';
  import type { DrivingEvent, MapInfo, PastDays, PlaceChoice, TimelineDay, TimelineGap, TimelineNote, TimelineStay, TimelineTrip, TravelMode } from '../../lib/api/types';
  import { kindIcon, kindLabel, speedingText, strength, trip as tripLine } from '../../lib/utils/driving';
  import { usesMiles } from '../../lib/stores/units.svelte';
  import { confirm } from '../../lib/stores/confirmDialog';
  import { clock } from '../../lib/utils/time';
  import { integer, t } from '../../lib/i18n/index.ts';
  import { categoryIcon, dayShort, distance, duration, localDate, modeIcon, modeLabel } from './format';
  import Export from './Export.svelte';
  import { followTheme, northUpMap, styleFor } from '../../lib/components/map/theme';
  import { palette } from '../../lib/utils/palette';
  import PlacePicker from './PlacePicker.svelte';
  import DayRing from './DayRing.svelte';
  import NoteEditor from './NoteEditor.svelte';

  // DayTab shows one day of a member's timeline as stays, trips, missing
  // visits, and paused stretches (005 FR-009, 005 FR-010), with each trip's route on
  // the map. Days in the long-term timeline say when their routes are
  // simplified. The member corrects their own days here (005 FR-041), and on
  // their own sees their notes, the day's hours by kind of place, and the
  // same date in earlier years (spec 005).
  let { groupId, userId, self, initialDate, initialEntry }: {
    groupId?: string;
    userId: string;
    self: boolean;
    initialDate?: string;
    // initialEntry is the stay or trip to pick once the day loads, as a
    // search result or a record opens it.
    initialEntry?: string;
  } = $props();

  type Entry =
    | { kind: 'stay'; start: string; stay: TimelineStay }
    | { kind: 'trip'; start: string; trip: TimelineTrip }
    | { kind: 'gap'; start: string; gap: TimelineGap };

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // miles is whether distance and speed show in miles (010 FR-034).
  let miles = $derived(usesMiles());
  const today = localDate(new Date());
  let date = $state(untrack(() => initialDate ?? today));
  let day = $state<TimelineDay | null>(null);
  let loading = $state(false);
  let error = $state('');
  let selected: number | null = $state(null);
  let exporting = $state(false);
  let pending = untrack(() => initialEntry);
  let past = $state<PastDays['days']>([]);
  let listEl: HTMLElement | undefined = $state();
  // addresses holds looked-up street addresses for stays the server could
  // not name yet, keyed by an 11 m cell like the server's cache.
  let addresses: Record<string, string> = $state({});
  // events are the member's own driving events for the day (spec 004 FR-050).
  let events: DrivingEvent[] = $state([]);
  let eventMarkers: maplibregl.Marker[] = [];

  let mapEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let stopTheme: (() => void) | null = null;
  let mapReady = $state(false);

  const entries: Entry[] = $derived.by(() => {
    if (!day) return [];
    const all: Entry[] = [
      ...day.stays.map((stay) => ({ kind: 'stay' as const, start: stay.start, stay })),
      ...day.trips.map((trip) => ({ kind: 'trip' as const, start: trip.start, trip })),
      ...day.gaps.map((gap) => ({ kind: 'gap' as const, start: gap.start, gap })),
    ];
    return all.sort((a, b) => a.start.localeCompare(b.start));
  });
  const detail = $derived(day?.trips.find((t) => t.detail_m)?.detail_m);

  onMount(async () => {
    const info = await api.get<MapInfo>('/map').catch(() => null);
    if (!mapEl) return;
    map = northUpMap({
      container: mapEl,
      style: styleFor(info),
      center: [-98.5, 39.8],
      zoom: 3,
      attributionControl: false,
    });
    // The fallback background has no sources to carry the attribution.
    addCredits(map, info, 'other');
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    // The first style draws and fits the day; a theme change only redraws.
    let fit = true;
    stopTheme = followTheme(map, info, () => {
      const peach = palette('peach');
      const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
      map!.addSource('routes', { type: 'geojson', data: empty });
      map!.addLayer({ id: 'routes', type: 'line', source: 'routes', filter: ['!', ['get', 'gap']],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': ['case', ['get', 'selected'], peach, palette('blue')], 'line-width': ['case', ['get', 'selected'], 6, 4], 'line-opacity': 0.85 } });
      map!.addLayer({ id: 'routes-gap', type: 'line', source: 'routes', filter: ['get', 'gap'],
        paint: { 'line-color': palette('overlay2'), 'line-width': 2, 'line-dasharray': [2, 2] } });
      map!.addSource('speeding', { type: 'geojson', data: empty });
      map!.addLayer({ id: 'speeding', type: 'line', source: 'speeding', layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': palette('red'), 'line-width': 6 } });
      map!.addSource('stays', { type: 'geojson', data: empty });
      map!.addLayer({ id: 'stays', type: 'circle', source: 'stays',
        paint: { 'circle-radius': ['case', ['get', 'selected'], 10, 7], 'circle-color': ['case', ['get', 'selected'], peach, palette('green')],
          'circle-stroke-color': palette('base'), 'circle-stroke-width': 2 } });
      mapReady = true;
      draw(fit);
      fit = false;
    });
  });

  onDestroy(() => {
    stopTheme?.();
    map?.remove();
  });

  // A new member or day loads again.
  $effect(() => {
    load(userId, date);
  });

  $effect(() => {
    const d = date;
    past = [];
    if (!self) return;
    api.get<PastDays>(`/me/timeline/on-this-day?date=${d}&tz=${encodeURIComponent(tz)}`)
      .then((got) => { if (d === date) past = got.days; })
      .catch(() => {});
  });

  $effect(() => {
    if (mapReady) {
      void selected;
      draw(false);
    }
  });

  async function load(user: string, d: string) {
    loading = true;
    error = '';
    selected = null;
    const path = self
      ? `/me/timeline/days/${d}?tz=${encodeURIComponent(tz)}`
      : `/groups/${groupId}/members/${user}/timeline/days/${d}?tz=${encodeURIComponent(tz)}`;
    try {
      const got = await api.get<TimelineDay>(path);
      if (user !== userId || d !== date) return;
      day = got;
      events = [];
      if (self && got.trips.length) {
        const from = got.trips.reduce((a, t) => (t.start < a ? t.start : a), got.trips[0].start);
        const to = got.trips.reduce((a, t) => (t.end > a ? t.end : a), got.trips[0].end);
        const fetched = await api.get<DrivingEvent[]>(`/me/driving/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`).catch(() => []);
        if (user !== userId || d !== date) return;
        // Only drives carry driving events; a train or a bus ride shows none
        // (spec 005 FR-030).
        const drives = got.trips.filter((t) => t.mode === 'driving').map((t) => [Date.parse(t.start), Date.parse(t.end)]);
        events = fetched.filter((ev) => { const s = Date.parse(ev.started_at); return drives.some(([a, b]) => s >= a && s <= b); });
      }
      draw(true);
      lookUpAddresses(got);
      if (pending) {
        const i = entries.findIndex((e) => (e.kind === 'stay' && e.stay.id === pending) || (e.kind === 'trip' && e.trip.id === pending));
        pending = undefined;
        if (i >= 0) {
          focus(i);
          await tick();
          listEl?.querySelector(`[data-entry="${i}"]`)?.scrollIntoView({ block: 'nearest' });
        }
      }
    } catch (e: any) {
      error = e.message;
      day = null;
    } finally {
      loading = false;
    }
  }

  // lookUpAddresses asks for addresses of stays the server has not named yet,
  // one at a time, since the server keeps its geocoder to one request a second.
  async function lookUpAddresses(d: TimelineDay) {
    for (const s of d.stays) {
      if (s.name || s.address) continue;
      const key = cell(s.lat, s.lon);
      if (key in addresses) continue;
      const a = await api.get<{ label: string }>(`/geocode/reverse?lat=${s.lat}&lon=${s.lon}`).catch(() => null);
      if (day !== d) return;
      addresses[key] = a?.label ?? '';
    }
  }

  function draw(fit: boolean) {
    if (!map || !mapReady) return;
    const routes: GeoJSON.Feature[] = [];
    const stays: GeoJSON.Feature[] = [];
    const bounds = new maplibregl.LngLatBounds();
    entries.forEach((e, i) => {
      if (e.kind === 'trip' && e.trip.route.length > 1) {
        const coords = e.trip.route.map((p) => [p.lon, p.lat] as [number, number]);
        routes.push({ type: 'Feature', properties: { selected: i === selected, gap: !!e.trip.gap }, geometry: { type: 'LineString', coordinates: coords } });
        coords.forEach((c) => bounds.extend(c));
      } else if (e.kind === 'stay') {
        stays.push({ type: 'Feature', properties: { selected: i === selected }, geometry: { type: 'Point', coordinates: [e.stay.lon, e.stay.lat] } });
        bounds.extend([e.stay.lon, e.stay.lat]);
      }
    });
    (map.getSource('routes') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features: routes });
    (map.getSource('stays') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features: stays });
    drawEvents();
    if (fit && !bounds.isEmpty()) map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 0 });
  }

  // drawEvents draws the member's own events: speeding stretches over the
  // route and a marker where each other event happened.
  function drawEvents() {
    if (!map) return;
    const points = entries.flatMap((e) => (e.kind === 'trip' ? e.trip.route : []));
    const stretches: GeoJSON.Feature[] = [];
    for (const ev of events.filter((x) => x.kind === 'speeding')) {
      const from = Date.parse(ev.started_at), to = Date.parse(ev.ended_at);
      const coords = points.filter((p) => { const t = Date.parse(p.at); return t >= from && t <= to; }).map((p) => [p.lon, p.lat]);
      if (coords.length > 1) stretches.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } });
    }
    (map.getSource('speeding') as maplibregl.GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: stretches });
    eventMarkers.forEach((m) => m.remove());
    eventMarkers = events.map((ev) => {
      const el = document.createElement('div');
      el.className = 'event-marker';
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', t('timelineEventAt', { event: eventTitle(ev), time: clock(ev.started_at) }));
      el.title = el.getAttribute('aria-label')!;
      const icon = document.createElement('i');
      icon.setAttribute('aria-hidden', 'true');
      icon.className = `las ${kindIcon(ev.kind)}`;
      el.append(icon);
      return new maplibregl.Marker({ element: el }).setLngLat([ev.lon, ev.lat]).addTo(map!);
    });
  }

  function eventTitle(ev: DrivingEvent): string {
    if (ev.kind === 'speeding') return speedingText(ev.value, ev.limit_mps, miles);
    return ev.value > 0 ? t('timelineEventStrength', { event: kindLabel(ev.kind), strength: strength(ev.value, miles) }) : kindLabel(ev.kind);
  }

  function eventsIn(t: TimelineTrip): DrivingEvent[] {
    const a = Date.parse(t.start), b = Date.parse(t.end);
    return events.filter((ev) => { const s = Date.parse(ev.started_at); return s >= a && s <= b; });
  }

  // setPassenger marks or unmarks the member's own drive (spec 004 FR-052).
  async function setPassenger(t: TimelineTrip, on: boolean) {
    actionError = '';
    try {
      if (on) await api.put('/me/driving/passenger', { start: t.start, end: t.end });
      else await api.delete(`/me/driving/passenger?start=${encodeURIComponent(t.start)}&end=${encodeURIComponent(t.end)}`);
      await load(userId, date);
    } catch (e: any) {
      actionError = e.message;
    }
  }

  function focus(i: number) {
    selected = i;
    const e = entries[i];
    if (!map || !e) return;
    if (e.kind === 'stay') {
      map.easeTo({ center: [e.stay.lon, e.stay.lat], zoom: Math.max(map.getZoom(), 15) });
    } else if (e.kind === 'trip' && e.trip.route.length > 1) {
      const b = new maplibregl.LngLatBounds();
      e.trip.route.forEach((p) => b.extend([p.lon, p.lat]));
      map.fitBounds(b, { padding: 60, maxZoom: 16 });
    }
  }

  function shift(days: number) {
    const [y, m, d] = date.split('-').map(Number);
    const next = localDate(new Date(y, m - 1, d + days));
    if (next <= today) date = next;
  }

  // Corrections. A day not built yet has no IDs, so the first correction
  // builds it and finds the entry again by kind and start.
  type Picking = {
    title: string;
    lat: number;
    lon: number;
    start?: string;
    end?: string;
    withTimes: boolean;
    save: (c: PlaceChoice, remember: boolean, start?: string, end?: string) => Promise<void>;
  };
  let picking = $state<Picking | null>(null);
  let actionError = $state('');

  async function withIds(e: Entry): Promise<Entry> {
    if (!day || (day.stays.every((s) => s.id) && day.trips.every((t) => t.id))) return e;
    day = await api.post<TimelineDay>(`/me/timeline/days/${date}/build?tz=${encodeURIComponent(tz)}`);
    const found = entries.find((x) => x.kind === e.kind && Math.abs(Date.parse(x.start) - Date.parse(e.start)) < 120_000);
    if (!found) throw new Error(t('timelineDayChanged'));
    return found;
  }

  async function act(run: () => Promise<void>) {
    actionError = '';
    try {
      await run();
      await load(userId, date);
    } catch (err: any) {
      actionError = err.message;
    }
  }

  function changePlace(e: Entry & { kind: 'stay' }) {
    picking = {
      title: t('timelineWhereWereYou'), lat: e.stay.lat, lon: e.stay.lon, withTimes: false,
      save: async (c, remember) => {
        const s = await withIds(e);
        if (s.kind !== 'stay') return;
        await api.patch(`/me/timeline/stays/${s.stay.id}`, { place: c, remember });
        await load(userId, date);
      },
    };
  }

  function fillIn(i: number) {
    const e = entries[i];
    const [lat, lon] = pointNear(i);
    picking = {
      title: t('timelineFillIn'), lat, lon, start: e.start, end: e.kind === 'stay' ? e.stay.end : e.kind === 'trip' ? e.trip.end : e.gap.end,
      withTimes: true,
      save: async (c, remember, start, end) => {
        await api.post('/me/timeline/stays', { start, end, tz, place: c, remember });
        await load(userId, date);
      },
    };
  }

  // Notes (spec 005 FR-042). A note on a stay or trip needs its ID, so
  // it builds the day first as a correction does.
  const notes = $derived(day?.notes ?? []);
  const dayNote = $derived(notes.find((n) => !n.stay_id && !n.trip_id));

  function noteOn(e: Entry): TimelineNote | undefined {
    if (e.kind === 'stay' && e.stay.id) return notes.find((n) => n.stay_id === e.stay.id);
    if (e.kind === 'trip' && e.trip.id) return notes.find((n) => n.trip_id === e.trip.id);
    return undefined;
  }

  async function saveDayNote(text: string) {
    await api.put('/me/timeline/notes', { date, text });
    await load(userId, date);
  }

  async function saveNote(e: Entry, text: string) {
    const x = await withIds(e);
    if (x.kind === 'stay') await api.put('/me/timeline/notes', { stay_id: x.stay.id, text });
    else if (x.kind === 'trip') await api.put('/me/timeline/notes', { trip_id: x.trip.id, text });
    await load(userId, date);
  }

  async function deleteNote(id: string) {
    await api.delete(`/me/timeline/notes/${id}`);
    await load(userId, date);
  }

  function setMode(e: Entry & { kind: 'trip' }, mode: TravelMode) {
    act(async () => {
      const t = await withIds(e);
      if (t.kind === 'trip') await api.patch(`/me/timeline/trips/${t.trip.id}`, { mode });
    });
  }

  async function remove(e: Entry) {
    if (!(await confirm(t('timelineDeleteConfirm', { what: e.kind })))) return;
    act(async () => {
      const x = await withIds(e);
      if (x.kind === 'stay') await api.delete(`/me/timeline/stays/${x.stay.id}`);
      else if (x.kind === 'trip') await api.delete(`/me/timeline/trips/${x.trip.id}`);
    });
  }

  // pointNear is where a missing visit probably was: where the member was
  // last seen before it, or first seen after it.
  function pointNear(i: number): [number, number] {
    const at = (e: Entry, last: boolean): [number, number] | null => {
      if (e.kind === 'stay') return [e.stay.lat, e.stay.lon];
      if (e.kind === 'trip' && e.trip.route.length) {
        const p = e.trip.route[last ? e.trip.route.length - 1 : 0];
        return [p.lat, p.lon];
      }
      return null;
    };
    for (let j = i - 1; j >= 0; j--) {
      const p = at(entries[j], true);
      if (p) return p;
    }
    for (let j = i + 1; j < entries.length; j++) {
      const p = at(entries[j], false);
      if (p) return p;
    }
    return [0, 0];
  }

  const modeChoices: TravelMode[] = ['walking', 'running', 'cycling', 'driving', 'bus', 'train', 'flight', 'other'];

  function cell(lat: number, lon: number): string {
    return `${lat.toFixed(4)},${lon.toFixed(4)}`;
  }

  function stayName(s: TimelineStay): string {
    return s.name || s.address || addresses[cell(s.lat, s.lon)] || `${s.lat.toFixed(4)}, ${s.lon.toFixed(4)}`;
  }

  // staySub is the line under a stay's name: its city and address when the
  // name is a place's own.
  function staySub(s: TimelineStay): string {
    if (!s.name) return s.city ?? '';
    return [s.address, s.city].filter(Boolean).join(', ');
  }

  // times is when an entry began and ended and how long it was; tag marks
  // a trip still going on, an imported entry, or both.
  function times(start: string, end: string, tag: 'latest' | 'imported' | 'both' | 'none' = 'none'): string {
    return t('timelineTimes', { start: clock(start), end: clock(end), time: duration(start, end), tag });
  }

  function tagOf(latest: boolean, imported: boolean): 'latest' | 'imported' | 'both' | 'none' {
    if (latest && imported) return 'both';
    return latest ? 'latest' : imported ? 'imported' : 'none';
  }
</script>

<div class="flex flex-col md:flex-row flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
  <aside class="w-full md:w-96 shrink-0 md:border-r flex flex-col md:min-h-0" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    <div class="p-4 space-y-3 border-b" style="border-color: var(--theme-surface0);">
      <div class="flex items-center gap-2">
        <button class="btn btn-secondary btn-sm" onclick={() => shift(-1)} aria-label={t('timelinePreviousDay')}><i aria-hidden="true" class="las la-angle-left"></i></button>
        <input type="date" class="input flex-1" max={today} bind:value={date} aria-label={t('timelineDay')} />
        <button class="btn btn-secondary btn-sm" onclick={() => shift(1)} disabled={date >= today} aria-label={t('timelineNextDay')}><i aria-hidden="true" class="las la-angle-right"></i></button>
      </div>
      {#if self}
        <button class="btn btn-secondary btn-sm" onclick={() => (exporting = true)}><i aria-hidden="true" class="las la-file-export mr-1"></i>{t('timelineExportTitle')}</button>
      {/if}
      {#if day?.simplified && day.trips.some((t) => !t.gap)}
        <p class="text-xs text-text-muted"><i aria-hidden="true" class="las la-compress-arrows-alt mr-1"></i>{detail ? t('timelineSimplifiedWithin', { meters: integer(detail) }) : t('timelineSimplified')}</p>
      {/if}
    </div>

    <div class="flex-1 md:overflow-y-auto" bind:this={listEl}>
      {#if self && day && !error}
        <div class="p-4 space-y-3 border-b" style="border-color: var(--theme-surface0);">
          <DayRing {day} />
          <div class="flex gap-2 flex-wrap items-center">
            <NoteEditor note={dayNote} on="day" showText onsave={saveDayNote} ondelete={deleteNote} />
          </div>
        </div>
      {/if}
      {#if error}
        <p class="p-4 text-sm" style="color: var(--theme-error);">{error}</p>
      {:else if loading && !day}
        <p class="p-4 text-sm text-text-muted">{t('timelineLoading')}</p>
      {:else if day && entries.length === 0}
        <p class="p-4 text-sm text-text-muted">{t('timelineNoLocationsDay')}</p>
      {:else if day}
        <ol>
          {#each entries as e, i (i)}
            {@const note = noteOn(e)}
            <li data-entry={i}>
              <button class="w-full text-left px-4 py-3 flex gap-3 items-start hover:opacity-80 {selected === i ? 'bg-surface0' : ''}" onclick={() => focus(i)}>
                {#if e.kind === 'stay'}
                  <i aria-hidden="true" class="las {categoryIcon(e.stay.category)} text-xl" style="color: var(--theme-green);"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block font-medium truncate">{stayName(e.stay)}</span>
                    {#if staySub(e.stay)}<span class="block text-xs text-text-muted truncate">{staySub(e.stay)}</span>{/if}
                    <span class="block text-xs text-text-muted">
                      {#if e.stay.ongoing}
                        {t('timelineStaySince', { start: clock(e.stay.start), end: clock(e.stay.end) })}
                      {:else}
                        {times(e.stay.start, e.stay.end, tagOf(false, e.stay.source === 'import'))}
                      {/if}
                    </span>
                    {#if note}<span class="block text-sm mt-1 whitespace-pre-wrap break-words"><i aria-hidden="true" class="las la-sticky-note mr-1 text-text-muted"></i><span class="sr-only">{`${t('timelineNoteSr')} `}</span>{note.text}</span>{/if}
                  </span>
                {:else if e.kind === 'trip' && (!e.trip.gap || e.trip.mode === 'flight')}
                  <i aria-hidden="true" class="las {modeIcon(e.trip.mode)} text-xl" style="color: var(--theme-blue);"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block font-medium">{t('timelineModeDistance', { mode: modeLabel(e.trip.mode), distance: distance(e.trip.distance_m) })}</span>
                    <span class="block text-xs text-text-muted">{times(e.trip.start, e.trip.end, tagOf(!!e.trip.ongoing, e.trip.source === 'import'))}</span>
                    {#if e.trip.driving}
                      {@const line = tripLine(e.trip.driving)}
                      {#if line.counts}<span class="block text-xs mt-0.5" style="color: var(--theme-accent);">{line.counts}</span>{/if}
                      {#each line.notes as n (n)}<span class="block text-xs text-text-muted">{n}</span>{/each}
                    {/if}
                    {#if note}<span class="block text-sm mt-1 whitespace-pre-wrap break-words"><i aria-hidden="true" class="las la-sticky-note mr-1 text-text-muted"></i><span class="sr-only">{`${t('timelineNoteSr')} `}</span>{note.text}</span>{/if}
                  </span>
                {:else if e.kind === 'gap' && e.gap.kind === 'paused'}
                  <i aria-hidden="true" class="las la-pause-circle text-xl" style="color: var(--theme-yellow);"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block font-medium">{t('timelineSharingPaused')}</span>
                    <span class="block text-xs text-text-muted">{times(e.gap.start, e.gap.end)}</span>
                  </span>
                {:else}
                  {@const s = e.kind === 'trip' ? e.trip : e.gap}
                  {@const missing = e.kind === 'gap' || e.trip.missing_visit}
                  <i aria-hidden="true" class="las la-question-circle text-xl" style="color: var(--theme-overlay2);"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block font-medium">{missing ? t('timelineMissingVisit') : t('timelineNoLocations')}</span>
                    <span class="block text-xs text-text-muted">{missing ? t('timelineTimesNoLocations', { start: clock(s.start), end: clock(s.end), time: duration(s.start, s.end) }) : times(s.start, s.end)}</span>
                  </span>
                {/if}
              </button>
              {#if self && selected === i}
                <div class="px-4 pb-3 pl-12 flex gap-2 flex-wrap items-center">
                  {#if e.kind === 'stay'}
                    <button class="btn btn-secondary btn-sm" onclick={() => changePlace(e)}><i aria-hidden="true" class="las la-edit mr-1"></i>{t('timelineChangePlace')}</button>
                    <button class="btn btn-secondary btn-sm" onclick={() => remove(e)}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('appDelete')}</button>
                    <NoteEditor {note} on="stay" onsave={(t) => saveNote(e, t)} ondelete={deleteNote} />
                  {:else if e.kind === 'trip' && (!e.trip.gap || e.trip.mode === 'flight')}
                    {#if e.trip.driving}
                      <ul class="w-full space-y-1 mb-1">
                        {#each eventsIn(e.trip) as ev (ev.started_at + ev.kind)}
                          <li class="text-sm"><i aria-hidden="true" class="las {kindIcon(ev.kind)} mr-1" style="color: var(--theme-error);"></i>{eventTitle(ev)} <span class="text-xs text-text-muted">{clock(ev.started_at)}</span></li>
                        {/each}
                      </ul>
                      <button class="btn btn-secondary btn-sm" onclick={() => setPassenger(e.trip, !e.trip.driving?.passenger)}>
                        <i aria-hidden="true" class="las la-user-friends mr-1"></i>{e.trip.driving.passenger ? t('timelineWasDriving') : t('timelineWasPassenger')}
                      </button>
                    {/if}
                    <select class="input text-sm py-1 w-36" value={e.trip.mode} aria-label={t('timelineWayOfTravel')}
                      onchange={(ev) => setMode(e, ev.currentTarget.value as TravelMode)}>
                      {#each modeChoices as m (m)}<option value={m}>{modeLabel(m)}</option>{/each}
                    </select>
                    <button class="btn btn-secondary btn-sm" onclick={() => remove(e)}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('appDelete')}</button>
                    <NoteEditor {note} on="trip" onsave={(t) => saveNote(e, t)} ondelete={deleteNote} />
                  {:else if e.kind === 'trip' || e.gap.kind === 'missing_visit'}
                    <button class="btn btn-secondary btn-sm" onclick={() => fillIn(i)}><i aria-hidden="true" class="las la-plus mr-1"></i>{t('timelineFillIn')}</button>
                    {#if e.kind === 'trip'}
                      <!-- A flight with a layover is too slow to be read as one, so a gap can be corrected to a flight (spec 005 FR-031). -->
                      <button class="btn btn-secondary btn-sm" onclick={() => setMode(e, 'flight')}><i aria-hidden="true" class="las la-plane mr-1"></i>{t('timelineWasFlight')}</button>
                    {/if}
                  {/if}
                </div>
                {#if actionError}<p class="px-4 pb-3 pl-12 text-sm" style="color: var(--theme-error);">{actionError}</p>{/if}
              {/if}
            </li>
          {/each}
        </ol>
      {/if}
      {#if self && past.length}
        <section class="p-4 border-t" style="border-color: var(--theme-surface0);" aria-labelledby="on-this-day">
          <h2 id="on-this-day" class="font-semibold text-sm mb-1">{t('timelineOnThisDay')}</h2>
          <ul>
            {#each past as p (p.date)}
              <li>
                <button class="w-full text-left py-2 flex items-center gap-3 hover:opacity-80" onclick={() => (date = p.date)}>
                  <span class="font-medium tabular-nums w-12">{p.date.slice(0, 4)}</span>
                  <span class="flex-1 min-w-0 text-sm text-text-muted truncate">{t('timelinePastDay', { day: dayShort(p.date), places: p.places, distance: distance(p.distance_m) })}</span>
                  <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                </button>
              </li>
            {/each}
          </ul>
        </section>
      {/if}
    </div>
  </aside>
  <div class="relative min-w-0 min-h-[50vh] md:min-h-0 md:flex-1">
    <div bind:this={mapEl} class="absolute inset-0"></div>
  </div>
</div>

{#if picking}
  <PlacePicker {...picking} onsave={picking.save} onclose={() => (picking = null)} />
{/if}

{#if exporting}
  <Export {date} onclose={() => (exporting = false)} />
{/if}

<style>
  :global(.event-marker) {
    width: 24px;
    height: 24px;
    border-radius: 9999px;
    background: var(--theme-red);
    border: 2px solid var(--theme-base);
    color: var(--theme-base);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
</style>
